// Work kit — Confluence page tree drag (wave 9, Track A).
//
// A NEW pragmatic controller on the W1 engine: it mounts on Ui::TreeComponent's
// frozen root <ul> through the `attach_controller` seam (the identifier swap IS
// the extension — the tree component itself is untouched) and wires
// @atlaskit/pragmatic-drag-and-drop programmatically per row/subtree, the way
// work_drag_controller (ui--work-drag) does for flat containers.
//
// The five reserved action names tree_component renders (dragStart/dragEnd/
// dragOver/dragLeave/drop) are implemented as guarded no-ops: p-dnd owns the
// gesture and the ONE monitor below is the only resolver — never double-apply.
//
// ── Affordances (the two Confluence drop affordances) ────────────────────────
// Each row is split into three vertical zones (pointer y vs row rect):
//   top/bottom quarter → thin sibling-slot line: border-t-2/border-b-2 +
//     border-primary (drop BEFORE/AFTER that row);
//   middle half        → wide nest highlight: bg-primary/10 + ring-2
//     ring-inset ring-primary (drop INTO that page's subtree);
// the children <ul> area of a parent → the same nest highlight (append at the
// end). A drop onto the space-root sentinel row (empty data-work-page-id)
// moves to the top level — container null.
//
// ── Events (anti-idea 2: events are the only mutation API) ──────────────────
// - `ui--work:page-move {container, moved_ids, before_id, after_id,
//   from_container, subtree, gesture_token, source}` — ONE event per accepted
//   move, neighbour deltas only (rank contract: never an absolute order). The
//   subtree (the dragged <li> with all descendants) is moved optimistically.
// - `ui--work:page-select {page_id}` — delegated row click (a row link
//   navigates instead: the href IS the selection).
// - The host answers with `ui--work:page-move-ack {page_id, ok, tree?,
//   gesture_token}` dispatched ON the root <ul> (this element — or any
//   descendant; the ack bubbles up, a document-level dispatch never reaches
//   it): `ok` adopts host truth (an optional full-def `tree` converges the
//   arrangement), `!ok` reverts byte-identically and shows exactly one toast.
//   `gesture_token` is REQUIRED — the token the move event carried is the only
//   key the pending journal is matched with; an ack without a known token
//   warns once and is ignored.
//
// ── Refusals ────────────────────────────────────────────────────────────────
// A drop into the dragged page's own subtree (cycle) or past the shell's
// data-work-page-tree-max-depth paints NO affordance and on drop: clears
// affordances, logs exactly one console.warn, emits NOTHING.
//
// ── Esc ─────────────────────────────────────────────────────────────────────
// Escape during a drag restores the pre-drag arrangement (byte-identical), no
// event. The listener binds at document CAPTURE and stops immediate
// propagation while a gesture is active — one Esc means "cancel the drag",
// never also "collapse the surrounding accordion/sidebar" (both bind
// bubble-phase document listeners that would otherwise run alongside or
// before this one). Keyboard: Alt+ArrowUp/Down reorder, Alt+ArrowLeft
// promotes, Alt+ArrowRight demotes under the previous sibling — same
// page-move event, form-field-guarded, live-region announced.

import {
  autoScrollForElements,
  autoScrollWindowForElements,
} from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element"
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { Controller } from "@hotwired/stimulus"

import { showToast, clearToasts } from "../../../lib/work/mutation_seam.js"
import { appendSlot, neighbourSlot } from "../../../lib/work/rank.js"
import { ITEM_TYPE, isWorkDragSource, resolveMove } from "../../../lib/work/routing.js"

// Event names (spec-pinned "ui--work:*" vocabulary).
const PAGE_MOVE_EVENT = "ui--work:page-move"
const PAGE_SELECT_EVENT = "ui--work:page-select"
const PAGE_MOVE_ACK_EVENT = "ui--work:page-move-ack"

// Affordance grammar — the same classes the W1 kernel paints (EDGE/NEST), so
// both surfaces read identically. Kept in frozen-substrate vocabulary order.
const EDGE_CLASSES = ["border-t-2", "border-b-2", "border-primary"]
const NEST_CLASSES = ["bg-primary/10", "ring-2", "ring-inset", "ring-primary"]
const MUTED_CLASS = "opacity-40"

// Mirror of tree_component.rb sub_classes (frozen substrate) for the children
// <ul> the controller CREATES when a page is nested under a leaf that never
// rendered one — cloning the nearest existing children ul's classes first.
const SUB_CLASSES =
  "ml-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-border pl-2.5 pr-0 py-0.5"

// Snapshot journals kept per gesture_token (F4 seam); FIFO evicted. The
// pending window is the LAST 20 gestures: a !ok ack arriving after 20 later
// gestures finds no journal and is warned + ignored (see _handleAck).
const SNAPSHOT_CAP = 20

// Quarter-zone split of a row (Confluence affordances).
const ZONE_RATIO = 0.25

// English fallbacks — every shell renders kit i18n copy (see component), these
// only cover a hand-mounted controller. %{token} interpolated below.
const DEFAULT_ANNOUNCEMENTS = {
  drag_started: "Moving %{label}",
  dropped: "Moved %{label}",
  moved_up: "Moved %{label} up",
  moved_down: "Moved %{label} down",
  promoted: "Moved %{label} up a level",
  demoted: "Moved %{label} down a level",
  nested: "Moved %{label} into %{parent}",
  cancelled: "Cancelled the move",
}
const DEFAULT_MOVE_FAILED = "Couldn't move %{label}. Please try again."

// Cross-root isolation: every connected controller instance gets a unique
// root_id carried in its sources/targets' drag data (W1 kernel pattern).
let rootCounter = 0
let tokenCounter = 0

function newGestureToken() {
  tokenCounter += 1
  return `ui--work-page-tree-gesture-${tokenCounter}`
}

export default class PageTreeDragController extends Controller {
  static targets = ["dropZone", "children"]

  // State init lives in initialize() because Stimulus fires target callbacks
  // BEFORE connect(): dropZoneTargetConnected must see populated cleanup maps.
  initialize() {
    this._rowCleanups = new Map()
    this._childrenCleanups = new Map()
    this._snapshots = new Map()
    this._gesture = null
    this._painted = null
  }

  connect() {
    // Idempotent: Turbo restoration re-runs connect on the same element.
    if (this._connected) return
    this._connected = true

    // initialize() owns first-run creation; disconnect() nulls these, so a
    // reconnect (Turbo restore, manual) must rebuild them.
    this._rowCleanups ||= new Map()
    this._childrenCleanups ||= new Map()
    this._snapshots ||= new Map()

    rootCounter += 1
    this._rootId = `ui--work-page-tree-root-${rootCounter}`

    this._resolveShellConfig()

    this._onKeydown = (event) => this._handleKeydown(event)
    this._onClick = (event) => this._handleClick(event)
    this._onAck = (event) => this._handleAck(event)
    this.element.addEventListener("keydown", this._onKeydown)
    this.element.addEventListener("click", this._onClick)
    this.element.addEventListener(PAGE_MOVE_ACK_EVENT, this._onAck)

    this._unbindWindowScroll = autoScrollWindowForElements()
    this._unbindMonitor = monitorForElements({
      canMonitor: ({ source }) => this._isOwnSource(source) && !this._gesture,
      onDragStart: ({ source }) => this._startGesture(source),
      // Monitors receive the full location, never a single target. The
      // pointer's INNERMOST over-target is dropTargets[0] (bubble order) — the
      // only element that may paint: ancestor children <ul>s of the hovered
      // row are also registered targets and must stay clean. onDropTargetChange
      // is synchronous (fires whenever the over-set changes); onDrag re-runs it
      // per frame so a zone change WITHIN one row repaints without a re-enter.
      onDropTargetChange: ({ source, location }) =>
        this._hover(source, location.current.dropTargets[0] ?? null),
      onDrag: ({ source, location }) =>
        this._hover(source, location.current.dropTargets[0] ?? null),
      onDrop: ({ source, location }) =>
        this._commitDrop(source, location.current.dropTargets[0] ?? null),
    })

    // Rewire targets that connected before connect() ran and were unwired by
    // a previous disconnect (manual or Turbo restore). The has() guards in the
    // target callbacks make this idempotent against the pre-connect callbacks.
    this.dropZoneTargets.forEach((row) => this.dropZoneTargetConnected(row))
    this.childrenTargets.forEach((ul) => this.childrenTargetConnected(ul))
  }

  disconnect() {
    if (!this._connected) return
    this._connected = false

    this._closeGesture()
    this._unbindMonitor?.()
    this._unbindWindowScroll?.()
    this._rowCleanups.forEach((fn) => fn())
    this._childrenCleanups.forEach((fn) => fn())
    this._rowCleanups = null
    this._childrenCleanups = null
    this.element.removeEventListener("keydown", this._onKeydown)
    this.element.removeEventListener("click", this._onClick)
    this.element.removeEventListener(PAGE_MOVE_ACK_EVENT, this._onAck)
    this._snapshots.clear()
    this._snapshots = null
    this._shell = null
  }

  // Shell config (the frozen root <ul> has no value passthrough): max depth,
  // live-region copy, toast copy and the regions themselves ride the
  // data-work-page-tree-* attributes of the component's shell div.
  _resolveShellConfig() {
    this._shell = this.element.closest("[data-work-page-tree-shell]")

    let announcements = {}
    try {
      announcements = JSON.parse(this._shell?.getAttribute("data-work-page-tree-announcements") || "{}") || {}
    } catch {
      announcements = {}
    }
    this._announcements = { ...DEFAULT_ANNOUNCEMENTS, ...announcements }

    const rawDepth = Number(this._shell?.getAttribute("data-work-page-tree-max-depth"))
    this._maxDepth = Number.isInteger(rawDepth) && rawDepth > 0 ? rawDepth : null

    this._moveFailed =
      this._shell?.getAttribute("data-work-page-tree-error") || DEFAULT_MOVE_FAILED
    this._announcer = this._shell?.querySelector("[data-work-page-tree-announcer]") || null
    this._toastRegion = this._shell?.querySelector("[data-work-page-tree-toast-region]") || null
  }

  // ── Target lifecycle (fires per row / per children <ul>, incl. Turbo re-adds)

  dropZoneTargetConnected(row) {
    this._rowCleanups ||= new Map()
    if (this._rowCleanups.has(row)) return
    const li = row.closest("li[data-work-page-id]")
    if (!li) return
    this._rowCleanups.set(row, this._pageId(li) === "" ? this._wireSentinelRow(row) : this._wireRow(row, li))
  }

  dropZoneTargetDisconnected(row) {
    const cleanup = this._rowCleanups?.get(row)
    if (cleanup) {
      cleanup()
      this._rowCleanups.delete(row)
    }
  }

  childrenTargetConnected(ul) {
    this._childrenCleanups ||= new Map()
    if (this._childrenCleanups.has(ul)) return

    const unbindDrop = dropTargetForElements({
      element: ul,
      canDrop: ({ source }) => this._isOwnSource(source),
      // Live container id: the static parent attr can go stale mid-session —
      // the DOM is the only truth (getData is lazily evaluated per drag).
      getData: () => ({ type: ITEM_TYPE, container_id: this._liveContainerId(ul), zone: "container" }),
    })
    const unbindScroll = autoScrollForElements({
      element: ul,
      canScroll: ({ source }) => this._isOwnSource(source),
    })
    this._childrenCleanups.set(ul, () => {
      unbindDrop()
      unbindScroll()
    })
  }

  childrenTargetDisconnected(ul) {
    const cleanup = this._childrenCleanups?.get(ul)
    if (cleanup) {
      cleanup()
      this._childrenCleanups.delete(ul)
    }
  }

  // The space-root sentinel (empty data-work-page-id): a top-level container —
  // dropping anywhere on it moves the page to the root level. Never draggable.
  _wireSentinelRow(row) {
    return dropTargetForElements({
      element: row,
      canDrop: ({ source }) => this._isOwnSource(source),
      getData: () => {
        const li = row.closest("li[data-work-page-id]")
        return { type: ITEM_TYPE, container_id: this._liveContainerId(li?.parentElement), zone: "container" }
      },
    })
  }

  // A page row: three-zone drop target + the drag handle wiring (the <li> is
  // the draggable; the row is the handle — p-dnd's dragHandle containment rule).
  _wireRow(row, li) {
    let wired = false
    if (!li.dataset.pageTreeDragWired) {
      li.dataset.pageTreeDragWired = "true"
      wired = true
    }

    const unbindDraggable = wired
      ? draggable({
          element: li,
          dragHandle: row,
          canDrag: () => this._pageId(li) !== "" && !this._gesture,
          getInitialData: () => ({
            type: ITEM_TYPE,
            root_id: this._rootId,
            item_id: this._pageId(li),
            container_id: this._liveContainerId(li.parentElement),
            subtree: "true",
          }),
        })
      : null

    const unbindDrop = dropTargetForElements({
      element: row,
      canDrop: ({ source }) => this._isOwnSource(source),
      getData: ({ input, element }) => {
        const rect = element.getBoundingClientRect()
        const zone = this._zoneFor(input, rect)
        const data = {
          type: ITEM_TYPE,
          item_id: this._pageId(li),
          container_id: this._liveContainerId(li.parentElement),
          zone,
        }
        if (zone === "top" || zone === "bottom") {
          // Quarters, not p-dnd's halves: a synthetic pointer pins the closest
          // edge to the zone the pointer actually signalled.
          return attachClosestEdge(data, {
            element,
            input: { clientX: input?.clientX ?? 0, clientY: zone === "top" ? rect.top + 1 : rect.bottom - 1 },
            allowedEdges: [zone === "top" ? "top" : "bottom"],
          })
        }
        return data
      },
    })

    return () => {
      unbindDrop()
      unbindDraggable?.()
      if (wired) delete li.dataset.pageTreeDragWired
    }
  }

  // ── Gesture lifecycle (the monitor is the ONLY resolver) ───────────────────

  _startGesture(source) {
    // Late flushes (p-dnd schedules onDragStart on a frame; a tear-down racing
    // it leaves a dangling schedule) must not resurrect a dead controller.
    if (!this._snapshots || this._gesture) return
    const li = source.element
    const token = newGestureToken()
    const label = this._labelOf(li)

    const journal = this._capture(li, label)
    journal.token = token // the ack/revert seam is keyed on this token
    this._snapshots.set(token, journal)
    this._evictSnapshots()
    this._gesture = { token, li, label }
    li.classList.add(MUTED_CLASS)
    this._announce("drag_started", { label })
    this._bindEsc()
  }

  _hover(source, target) {
    this._clearAffordances()
    if (!this._gesture || !target) return
    const desc = this._intentFor(source, target)
    if (!desc || desc.ok === false) return

    const { intent } = desc
    if (intent.kind === "container") {
      this._paint(target.element, NEST_CLASSES)
    } else if (intent.zone === "middle") {
      this._paint(target.element, NEST_CLASSES)
    } else {
      // Sibling slot: the thin line on the signalled edge only.
      this._paint(target.element, intent.zone === "top"
        ? [EDGE_CLASSES[0], EDGE_CLASSES[2]]
        : [EDGE_CLASSES[1], EDGE_CLASSES[2]])
    }
  }

  _commitDrop(source, target) {
    if (!this._gesture) return
    const gesture = this._gesture
    const desc = target ? this._intentFor(source, target) : null

    // Nothing to publish: drop outside everything (p-dnd's cancel shape) or a
    // self-drop — close quietly, no event, no warn.
    if (!desc) {
      this._closeGesture()
      return
    }

    // Refusal (cycle / max depth): clear affordances, one warn, NO event.
    if (desc.ok === false) {
      this._closeGesture()
      console.warn(`[ui--work-page-tree] refused drop: ${desc.reason === "cycle" ? "cannot move a page into its own subtree" : "exceeds max depth"}`)
      return
    }

    this._applyMove(gesture.li, desc.intent, this._snapshots.get(gesture.token), "pointer")
    this._closeGesture()
  }

  _closeGesture() {
    this._unbindEsc()
    this._clearAffordances()
    if (this._gesture) {
      this._dropClasses(this._gesture.li, [MUTED_CLASS])
      this._gesture = null
    }
  }

  // Esc during a drag: restore the pre-drag arrangement byte-identically, no
  // event. Flag-guarded document listener (W1 kernel pattern) bound at
  // CAPTURE: the accordion's and sidebar's Esc handlers are document-level
  // bubble listeners bound at load, so a bubble-phase binding here would lose
  // the race on registration order and the chrome would collapse mid-drag.
  _bindEsc() {
    if (this._escBound) return
    this._escBound = true
    this._escHandler = (event) => this._handleEsc(event)
    document.addEventListener("keydown", this._escHandler, { capture: true })
  }

  _unbindEsc() {
    if (!this._escBound) return
    this._escBound = false
    document.removeEventListener("keydown", this._escHandler, { capture: true })
    this._escHandler = null
  }

  _handleEsc(event) {
    if (event.key !== "Escape" || !this._gesture) return
    // Gesture veto: consume the key for everyone — no listener after this one
    // (same node or any later phase) may also act on this Esc.
    event.stopImmediatePropagation()
    if (typeof event.cancelable !== "boolean" || event.cancelable) event.preventDefault()
    const token = this._gesture.token
    this._closeGesture()
    this.revertMove(token)
    this._announce("cancelled")
  }

  // ── Intent resolution ───────────────────────────────────────────────────────
  // Returns null (no publishable intent: no target / self-drop / unresolvable
  // slot), { ok: false, reason: "cycle" | "depth" } (refusal), or
  // { ok: true, intent: {kind, containerId, beforeId, afterId, zone,
  // announceKey, tokens} }.

  _intentFor(source, target) {
    const desc = resolveMove({ source, target })
    const li = this._gesture?.li
    if (!desc || !li) return null
    const movedId = this._pageId(li)

    // Container-area drop (children <ul> or the sentinel row): append at the
    // end of that container.
    if (desc.kind === "container") {
      const element = target.element
      if (li.contains(element)) return { ok: false, reason: "cycle" }
      const containerId = this._liveContainerId(element)
      if (!this._depthOk(li, containerId)) return { ok: false, reason: "depth" }
      // The order source: a children <ul> IS the container; the sentinel ROW
      // only stands for the root-level container — its children <ul>.
      const orderEl = element.tagName === "UL"
        ? element
        : this._existingChildrenUl(element.closest("li[data-work-page-id]") ?? this._sentinelLi())
      const slot = appendSlot({ order: orderEl ? this._orderOf(orderEl) : [], movedIds: [movedId] })
      return {
        ok: true,
        intent: { kind: "container", containerId, beforeId: slot.before_id, afterId: slot.after_id, announceKey: "dropped", tokens: {} },
      }
    }

    const rowLi = target.element.closest("li[data-work-page-id]")
    if (!rowLi) return null
    const zone = target.data.zone ?? "bottom"

    // Middle half: nest INTO that page (append to its children). The sentinel
    // never takes this path — its row is wired as a container target.
    if (zone === "middle") {
      if (rowLi === li || li.contains(rowLi)) return { ok: false, reason: "cycle" }
      const containerId = this._pageId(rowLi)
      if (!this._depthOk(li, containerId)) return { ok: false, reason: "depth" }
      const existing = this._existingChildrenUl(rowLi)
      const slot = appendSlot({ order: existing ? this._orderOf(existing) : [], movedIds: [movedId] })
      return {
        ok: true,
        intent: {
          kind: "item", zone, containerId, beforeId: slot.before_id, afterId: slot.after_id,
          announceKey: "nested", tokens: { parent: this._labelOf(rowLi) },
        },
      }
    }

    // Top/bottom quarter: sibling slot relative to that row.
    // A drop onto a DESCENDANT row's edge is the same cycle the middle path
    // refuses — resolveMove only compares ids, so the subtree guard must be
    // repeated here or _hover paints a sibling slot _applyMove silently
    // rejects (refusal contract: no paint, ONE console.warn, no event).
    if (li.contains(rowLi)) return { ok: false, reason: "cycle" }
    // The slot lands at the ROW's depth — a dragged page carrying a subtree
    // can exceed max_depth even though the slot itself looks shallow. The
    // container and middle paths check the same way; skipping the guard here
    // let a deep edge drop re-parent past max_depth with the affordance
    // painted and the event emitted.
    const containerId = this._liveContainerId(rowLi.parentElement)
    if (!this._depthOk(li, containerId)) return { ok: false, reason: "depth" }
    const edge = desc.edge ?? zone
    const slot = neighbourSlot({
      order: this._orderOf(rowLi.parentElement),
      movedIds: [movedId],
      targetId: this._pageId(rowLi),
      edge,
    })
    if (!slot) return null
    return {
      ok: true,
      intent: {
        kind: "item", zone, containerId,
        beforeId: slot.before_id, afterId: slot.after_id,
        announceKey: "dropped", tokens: {},
      },
    }
  }

  // Quarter-zone lookup against the row rect (p-dnd's closest edge is
  // half-based; the Confluence affordance needs quarters).
  _zoneFor(input, rect) {
    if (!input || typeof input.clientY !== "number") return "middle"
    const band = rect.height * ZONE_RATIO
    if (input.clientY < rect.top + band) return "top"
    if (input.clientY > rect.bottom - band) return "bottom"
    return "middle"
  }

  // Guards shared by drop + keyboard paths.

  _depthOk(li, containerId) {
    if (this._maxDepth == null) return true
    const newDepth = containerId == null ? 1 : this._depthOfPage(containerId) + 1
    return newDepth + this._levelsBelow(li) - 1 <= this._maxDepth
  }

  _depthOfPage(pageId) {
    let walker = this._liFor(pageId)
    let depth = 0
    while (walker) {
      walker = walker.parentElement?.closest("li[data-work-page-id]") ?? null
      if (walker && this._pageId(walker) !== "") depth += 1
    }
    return depth + 1
  }

  // The dragged subtree's height in levels (a leaf is 1): the MAX over every
  // direct-child page li, so an uneven branch (a shallow first branch, a
  // deeper later one) is never undercounted — the old single-chain walk
  // followed only the first descendant ul per level and made _depthOk
  // permissive past max_depth on the checked paths.
  _levelsBelow(li) {
    const ul = this._existingChildrenUl(li)
    const children = ul ? this._siblingLis(ul) : []
    if (!children.length) return 1
    return 1 + Math.max(...children.map((child) => this._levelsBelow(child)))
  }

  // ── Optimistic apply (drop + keyboard share this path) ─────────────────────

  // Moves the dragged subtree (ONE <li>, descendants ride along), journals the
  // mutations for a byte-identical revert, emits exactly ONE page-move event
  // and announces. Returns whether the move happened.
  _applyMove(li, intent, journal, source) {
    if (!journal || !li) return false

    const parentLi = intent.containerId == null ? this._sentinelLi() : this._liFor(intent.containerId)
    if (!parentLi) return false
    const parentUl = this._childrenUlFor(parentLi, journal)
    if (!parentUl) return false
    if (parentLi === li || li.contains(parentLi)) return false

    // Neighbour-delta contract (rank.js): before_id/after_id are the LEFT and
    // RIGHT neighbours bracketing the new slot. Land AFTER the left neighbour
    // (append when it has none), else BEFORE the right one.
    let refNode = null
    if (intent.beforeId != null) {
      // A NAMED neighbour must resolve inside the destination ul: a ghost id
      // would otherwise fall through to insertBefore(li, null) — an append at
      // the parent's end the caller never asked for. Both-null (empty-ul
      // append) keeps the legit append below.
      const left = this._liWithin(parentUl, intent.beforeId)
      if (!left) return false
      refNode = left.nextSibling
    } else if (intent.afterId != null) {
      const right = this._liWithin(parentUl, intent.afterId)
      if (!right) return false
      refNode = right
    }
    if (refNode === li) return false

    parentUl.insertBefore(li, refNode)
    journal.moved = true
    li.dataset.workParentId = intent.containerId ?? ""

    // Open the ancestors the move lands under (revert restores their state).
    let walker = parentUl
    while (walker) {
      const host = walker.parentElement?.closest("li") ?? null
      if (!host) break
      const details = host.querySelector(":scope > details")
      if (details && !details.open) {
        journal.touched.push({ el: details, attr: "open", value: false })
        details.open = true
      }
      walker = host.parentElement
    }

    this._refreshTreeFlags()
    this._emit(PAGE_MOVE_EVENT, {
      container: intent.containerId ?? null,
      moved_ids: [this._pageId(li)],
      before_id: intent.beforeId ?? null,
      after_id: intent.afterId ?? null,
      from_container: journal.fromContainerId ?? null,
      subtree: true,
      gesture_token: journal.token,
      source,
    })
    this._announce(intent.announceKey, { label: journal.label, ...intent.tokens })
    return true
  }

  // Journal a pre-move state: where the li sat, what its attrs were.
  _capture(li, label) {
    const parentIdAttr = li.dataset.workParentId ?? ""
    return {
      token: null,
      label,
      touched: [],
      createdUls: [],
      moved: false,
      // Pristine pre-gesture copy of the moved li (captured before the mute
      // class). The optimistic move + revert cycle re-binds p-dnd on the
      // original li — removing the created children <ul> disconnects its row,
      // and the reconnect re-appends the `draggable` attribute (value-exact,
      // byte-drifted). Swapping in the clone keeps the revert byte-exact; the
      // wiring marker is stripped from the clone AND every descendant (a
      // deep clone carries the marker on each descendant <li>) so every row
      // re-registers with p-dnd on connect — a surviving marker makes
      // _wireRow skip draggable() and the row stays undraggable forever.
      liClone: (() => {
        const clone = li.cloneNode(true)
        clone.removeAttribute("data-page-tree-drag-wired")
        clone
          .querySelectorAll("[data-page-tree-drag-wired]")
          .forEach((el) => el.removeAttribute("data-page-tree-drag-wired"))
        return clone
      })(),
      // The container the gesture STARTS from — read from the live DOM (the
      // static attr can be stale after prior optimistic moves).
      fromContainerId: this._liveContainerId(li.parentElement),
      entry: {
        li,
        parent: li.parentElement,
        next: li.nextSibling,
        parentIdAttr,
      },
    }
  }

  _evictSnapshots() {
    while (this._snapshots.size > SNAPSHOT_CAP) {
      const oldest = this._snapshots.keys().next().value
      this._snapshots.delete(oldest)
    }
  }

  // Keep the has-children flags honest after any structural change. Idempotent:
  // only writes attributes whose truth actually changed.
  _refreshTreeFlags() {
    this.element.querySelectorAll("li[data-work-page-id]").forEach((li) => {
      const ul = this._existingChildrenUl(li)
      const hasChildren = Array.from(ul?.children ?? []).some(
        (child) => child.tagName === "LI" && this._pageId(child) !== "",
      )
      const value = hasChildren ? "true" : "false"
      if (li.dataset.workHasChildren !== value) li.dataset.workHasChildren = value
    })
  }

  // ── DOM lookups ─────────────────────────────────────────────────────────────

  _pageId(li) {
    return li?.dataset?.workPageId ?? ""
  }

  _liFor(pageId) {
    if (!pageId) return null
    const escaped = window.CSS?.escape ? CSS.escape(pageId) : pageId.replace(/["\\]/g, "\\$&")
    return this.element.querySelector(`li[data-work-page-id="${escaped}"]`)
  }

  // The space-root sentinel: the root <ul>'s single direct <li> (empty page id
  // by component contract) — its children ul IS the root-level container.
  _sentinelLi() {
    const li = Array.from(this.element.children).find((el) => el.tagName === "LI")
    return li && this._pageId(li) === "" ? li : null
  }

  // Direct-child <li> of `ul` with the given page id (never a deeper match).
  _liWithin(ul, pageId) {
    if (!ul || pageId == null) return null
    return Array.from(ul.children).find(
      (el) => el.tagName === "LI" && el.dataset.workPageId === pageId,
    ) ?? null
  }

  _orderOf(ul) {
    if (!ul) return []
    return Array.from(ul.children)
      .filter((el) => el.tagName === "LI" && el.dataset.workPageId !== "")
      .map((el) => el.dataset.workPageId)
  }

  // The container a <ul> belongs to: the nearest enclosing <li>'s page id, or
  // null when that li is the sentinel (root level). Reads the LIVE DOM.
  _liveContainerId(ul) {
    const hostLi = ul?.parentElement?.closest("li[data-work-page-id]") ?? null
    const id = this._pageId(hostLi)
    return id === "" ? null : id
  }

  _existingChildrenUl(li) {
    return li?.querySelector(":scope > details > ul") ?? li?.querySelector(":scope > ul") ?? null
  }

  // The children <ul> to insert into, creating one when the target page is a
  // leaf that never rendered one (classes cloned from the nearest existing
  // children ul, else the frozen-substrate mirror). Creations are journaled so
  // a revert removes them.
  _childrenUlFor(parentLi, journal) {
    const existing = this._existingChildrenUl(parentLi)
    if (existing) return existing
    const ul = document.createElement("ul")
    ul.className =
      this.element.querySelector("[data-ui--work-page-tree-target='children']")?.className || SUB_CLASSES
    ul.setAttribute("data-ui--work-page-tree-target", "children")
    parentLi.appendChild(ul)
    journal?.createdUls.push(ul)
    return ul
  }

  // The root-level container: the sentinel's children ul, created on demand
  // (callers only reach it when a page must be seated at root). Null when the
  // sentinel itself is missing (malformed substrate).
  _rootContainer() {
    const sentinel = this._sentinelLi()
    return sentinel ? this._childrenUlFor(sentinel, null) : null
  }

  // Foreign-rider rescue for the revert's discard paths: a page li still
  // inside `container` whose id the journal's pristine clone does NOT carry
  // arrived after the capture (a concurrent gesture's optimistic nest) — its
  // own gesture may already be acked ok, so no revert is left to seat it when
  // the container is discarded. Move each such rider to the sentinel's root
  // container (best-effort home: the page must never vanish from the tree).
  // Riders the clone DOES carry are skipped: their pristine copies ride in
  // the seated clone, so rescuing the originals would only strip those copies
  // out of the subtree (a split the revert is supposed to restore). Topmost-
  // only: `container.contains(rider)` is re-checked per iteration so the
  // descendants of an already-rescued rider ride along instead of being
  // re-rescued. `skip` is the journal's own li — the seat step replaces it
  // with its byte-exact clone.
  _rescueRiders(container, skip, cloneIds) {
    container.querySelectorAll("li[data-work-page-id]").forEach((rider) => {
      const riderId = this._pageId(rider)
      if (!riderId || rider === skip || cloneIds.has(riderId)) return
      if (!container.contains(rider)) return
      const rootUl = this._rootContainer()
      if (!rootUl) return
      rootUl.appendChild(rider)
      rider.dataset.workParentId = ""
    })
  }

  _labelOf(li) {
    const row = li?.querySelector("summary, a, button, div")
    const label = li?.querySelector("span.truncate")
    return (label?.textContent || row?.textContent || "").trim()
  }

  _isOwnSource(source) {
    return isWorkDragSource(source) && source.data.root_id === this._rootId
  }

  // ── Affordances ─────────────────────────────────────────────────────────────

  _paint(el, classes) {
    this._clearAffordances()
    classes.forEach((name) => el.classList.add(name))
    this._painted = { el, classes }
  }

  _clearAffordances() {
    if (!this._painted) return
    this._dropClasses(this._painted.el, this._painted.classes)
    this._painted = null
  }

  // Remove classes AND the emptied attribute itself: a revert must be
  // byte-identical, and classList add/remove leaves `class=""` behind.
  _dropClasses(el, names) {
    names.forEach((name) => el.classList.remove(name))
    if (el.getAttribute("class") === "") el.removeAttribute("class")
  }

  // ── Plumbing ────────────────────────────────────────────────────────────────

  _emit(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, cancelable: true }))
  }

  _announce(key, tokens = {}) {
    if (!this._announcer) return
    this._announcer.textContent = this._interpolate(this._announcements[key] ?? "", tokens)
  }

  _interpolate(message, tokens) {
    return String(message).replace(/%?\{(\w+)\}/g, (match, name) => tokens[name] ?? match)
  }

  // ── Public API (the F4 seam) ────────────────────────────────────────────────

  // Restore the pre-move arrangement exactly (F4 Layer 0): removes created
  // containers, restores journaled attribute/detail states, re-inserts the
  // moved li at its captured position. One-shot per token; returns whether the
  // token was known.
  revertMove(token) {
    const journal = token && this._snapshots?.get(token)
    if (!journal) return false
    this._snapshots.delete(token)

    // The page ids the pristine clone carries (captured BEFORE any concurrent
    // nest into the dragged subtree). A page li inside a subtree this revert
    // discards whose id is NOT here arrived after the capture — a foreign
    // rider whose own gesture may already be acked ok, leaving nobody to seat
    // it when the discard detaches it.
    const cloneIds = new Set(
      Array.from(journal.liClone.querySelectorAll("li[data-work-page-id]"))
        .map((el) => el.dataset.workPageId)
        .filter(Boolean),
    )

    journal.createdUls.forEach((ul) => {
      // Foreign-rider rescue: the created container may carry pages OTHER
      // gestures nested into it after this journal's capture — moves acked
      // ok, whose reverts no longer exist. Discarding it would detach them
      // with nobody left to seat them (vanish). This journal's own li stays:
      // the seat step below replaces it with its pristine clone.
      this._rescueRiders(ul, journal.entry.li, cloneIds)
      ul.remove()
    })
    ;[...journal.touched].reverse().forEach(({ el, attr, value }) => {
      if (attr === "open") el.open = value === true
      else el.setAttribute(attr, value)
    })
    const { li, parent, next, parentIdAttr } = journal.entry
    // Best-effort restore under concurrent gestures (M5): a concurrent
    // gesture's revert may have removed the captured parent (its createdUls
    // cleanup) or displaced the captured anchor — insertBefore with a node
    // that is no longer a child of the original parent throws NotFoundError
    // mid-revert (state half restored). Fall back to an append; when the
    // captured parent is gone entirely, seat at the sentinel's root
    // container instead — the page must never vanish from the tree.
    const anchor = next && next.parentElement === parent ? next : null
    if (journal.moved && journal.liClone) {
      // ── The seat algorithm (concurrency-safe revert) ──────────────────────
      // Discard the mutated original (its row cleanup unbinds p-dnd on
      // removal) and seat the pristine clone — the clone's rows re-register
      // with p-dnd when they connect. Focus inside the removed li falls to
      // <body> on removal; keep it on the clone's row so a keyboard gesture's
      // revert never drops focus. Two live lookups make the seat safe under
      // concurrent gestures, where OTHER reverts may have detached this page
      // or seated a copy of it:
      // 1. Seat decision — a live same-id lookup, never connectivity
      //    inference: seat when NO connected element carries the page id
      //    (detached is fine — the concurrent createdUls cleanup that took
      //    this page may also have taken created-ul SIBLINGS that belong to
      //    other gestures, and each of those seats itself on its own !ok) or
      //    the connected element found IS this journal's own li (the normal
      //    still-there path). Skip only when a DIFFERENT connected element
      //    carries the id: that concurrent revert already seated its own
      //    clone for this page — re-seating ours would duplicate it.
      // 2. Carried-descendant dedupe — the clone may deep-carry pages whose
      //    optimistic moves landed inside the dragged subtree AFTER capture
      //    (a later gesture nested into it). Those pages' own reverts seat
      //    the authoritative copies; strip any carried copy whose page id a
      //    connected element OUTSIDE the seated clone carries.
      // Convergence bound: these lookups keep EVERY ack interleaving
      // vanish-free and duplicate-free — that is the always-true promise.
      // "Restores the pre-move arrangement exactly" holds for overlapping
      // gestures acked in gesture order ONLY when no interleaved gesture
      // moved this gesture's captured anchor (within or out of its parent)
      // or removed its captured parent, or relocated a page the pristine
      // clone carries; otherwise (and always for out-of-order acks)
      // convergence is best-effort — a carried copy inside an already-seated
      // clone reads as "already seated" (the skip gate above), an anchor
      // moved out of its parent falls back to an append, and an anchor moved
      // within its parent seats the clone at the shifted position, so the
      // tree can settle on an intermediate arrangement. No page lost, none
      // duplicated, in every case.
      const seatUl = parent && parent.isConnected ? parent : this._rootContainer()
      if (seatUl) {
        const alreadySeated = this._liFor(this._pageId(li))
        if (!alreadySeated || alreadySeated === li) {
          // Foreign-rider rescue BEFORE the discard: pages nested into the
          // dragged subtree after the capture ride inside the mutated
          // original — the pristine clone doesn't carry them and, when their
          // own gesture was acked ok, no revert is left to seat them.
          // (Computed before hadFocus so focus that rode out with a rescued
          // rider doesn't trigger a bogus refocus.)
          this._rescueRiders(li, journal.entry.li, cloneIds)
          const hadFocus = li.contains(document.activeElement)
          li.remove()
          const seated = journal.liClone
          seatUl.insertBefore(seated, seatUl === parent ? anchor : null)
          if (seatUl !== parent) seated.dataset.workParentId = ""
          // One scan per revert (not per carried li): collect the ids of
          // every connected page OUTSIDE the seated clone, then strip
          // carried copies by set membership. A full scan, not the
          // first-match _liFor: a carried copy seated EARLIER in document
          // order than the authoritative one must still be recognised as
          // carried.
          const outsideIds = new Set(
            Array.from(this.element.querySelectorAll("li[data-work-page-id]"))
              .filter((el) => !seated.contains(el))
              .map((el) => el.dataset.workPageId)
              .filter(Boolean),
          )
          seated.querySelectorAll("li[data-work-page-id]").forEach((carried) => {
            const carriedId = this._pageId(carried)
            if (carriedId && outsideIds.has(carriedId)) carried.remove()
          })
          if (hadFocus) this._refocusRow(seated)
        }
      }
      this._refreshTreeFlags()
      return true
    }
    if (parent && parent.isConnected && (li.parentElement !== parent || (anchor && li.nextSibling !== anchor))) {
      // Only when actually displaced — insertBefore removes + re-inserts even
      // for an in-place call, which cycles the row's p-dnd wiring (the
      // reconnect re-appends the draggable attribute) and drifts the render.
      // A stale anchor (removed/adopted by a later gesture) resolves to null:
      // an in-place li stays put, a displaced one appends at the old parent's
      // end rather than crashing.
      parent.insertBefore(li, anchor)
    }
    li.dataset.workParentId = parentIdAttr
    this._refreshTreeFlags()
    return true
  }

  // Adopt host truth: reorder existing <li>s to the def's parent + order
  // (missing ids skipped). The optional `tree` on an `ok` ack.
  adoptTree(def) {
    if (!def || typeof def !== "object") return
    this._adoptNode(def)
    this._refreshTreeFlags()
  }

  _adoptNode(node) {
    const value = node?.value == null ? "" : String(node.value)
    const li = value === "" ? this._sentinelLi() : this._liFor(value)
    const children = Array.isArray(node?.children) ? node.children : []
    if (!li || !children.length) return
    // The children ul materialises lazily on the first ADOPTED child: a def
    // whose every child is skipped (missing ids, the cyclic child) must not
    // leave an empty, Stimulus-wired <ul> under a leaf.
    let ul = this._existingChildrenUl(li)
    children.forEach((child) => {
      const childId = child?.value == null ? "" : String(child.value)
      const childLi = childId === "" ? null : this._liFor(childId)
      // A cyclic def (a page nested under its own descendant) resolves to a
      // childLi that ANCESTORS the target li — appendChild would throw
      // HierarchyRequestError mid-convergence, leaving a half-adopted tree.
      // Same malformed-def class as missing ids: skip the cyclic child,
      // adopt the rest. contains() covers the self case too, and it is the
      // honest test here — the target ul may not exist yet.
      if (!childLi || childLi.contains(li)) return
      ul = ul ?? this._childrenUlFor(li, null)
      ul.appendChild(childLi)
      childLi.dataset.workParentId = value
      this._adoptNode(child)
    })
  }

  // ── Ack consumption (F4 seam: ok adopts, !ok reverts + one toast) ──────────

  _handleAck(event) {
    const ack = event.detail ?? {}
    const journal = ack.gesture_token && this._snapshots.get(ack.gesture_token)
    if (!journal) {
      // An ack without the token its move event carried can never match, and
      // pending journals evict FIFO after SNAPSHOT_CAP gestures — say so once
      // (the refusal grammar), then ignore: an unknown ack mutates nothing.
      console.warn("[ui--work-page-tree] ignored ui--work:page-move-ack: unknown gesture token")
      return
    }
    // The seam's ok contract is a strict boolean (mutation_seam.js ignores
    // non-boolean; dashboard_grid normalizes to !ok). A malformed ack —
    // ok: "yes", ok: 1 — is NOT an ok: coerce to false so it flows down the
    // revert path instead of adopting.
    const ok = typeof ack.ok === "boolean" ? ack.ok : false
    if (ok) {
      this._snapshots.delete(ack.gesture_token)
      clearToasts(this._toastRegion)
      if (ack.tree) this.adoptTree(ack.tree)
    } else {
      this.revertMove(ack.gesture_token)
      showToast(this._toastRegion, this._interpolate(this._moveFailed, { label: journal.label }), "error")
    }
  }

  // ── Keyboard (the guaranteed cross-input path) ─────────────────────────────

  _handleKeydown(event) {
    // Form-field guard: arrows mean caret/history inside inputs.
    if (event.target?.closest?.("input, select, textarea, [contenteditable='true']")) return
    if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (this._gesture) return
    const li = event.target?.closest?.("li[data-work-page-id]")
    const pageId = this._pageId(li)
    if (!li || pageId === "") return

    // Boundary FIRST: an Alt+arrow aimed at a page row is always ours —
    // the browser's history back/forward must never fire, even for moves we
    // refuse below (promote at root, demote with no previous sibling, a slot
    // past either end, past max_depth). The refusal stays a silent no-op; the
    // key consumption is not conditional on the move being available.
    if (
      event.key === "ArrowUp" || event.key === "ArrowDown"
      || event.key === "ArrowLeft" || event.key === "ArrowRight"
    ) {
      if (typeof event.cancelable !== "boolean" || event.cancelable) event.preventDefault()
    } else {
      return
    }

    let intent = null
    if (event.key === "ArrowUp") intent = this._keyboardSibling(li, pageId, "top")
    else if (event.key === "ArrowDown") intent = this._keyboardSibling(li, pageId, "bottom")
    else if (event.key === "ArrowLeft") intent = this._keyboardPromote(li, pageId)
    else if (event.key === "ArrowRight") intent = this._keyboardDemote(li, pageId)
    if (!intent) return
    if (!this._depthOk(li, intent.containerId)) return

    const journal = this._capture(li, this._labelOf(li))
    journal.token = newGestureToken()
    this._snapshots.set(journal.token, journal)
    this._evictSnapshots()
    this._applyMove(li, intent, journal, "keyboard")
    this._refocusRow(li)
  }

  _keyboardSibling(li, pageId, edge) {
    const siblings = this._siblingLis(li.parentElement)
    const neighbor = edge === "top" ? siblings[siblings.indexOf(li) - 1] : siblings[siblings.indexOf(li) + 1]
    if (!neighbor) return null
    const slot = neighbourSlot({
      order: siblings.map((el) => el.dataset.workPageId),
      movedIds: [pageId],
      targetId: this._pageId(neighbor),
      edge,
    })
    if (!slot) return null
    return {
      containerId: this._liveContainerId(li.parentElement),
      beforeId: slot.before_id, afterId: slot.after_id,
      announceKey: edge === "top" ? "moved_up" : "moved_down", tokens: {},
    }
  }

  _keyboardPromote(li, pageId) {
    const parentLi = li.parentElement?.closest("li[data-work-page-id]") ?? null
    if (!parentLi || this._pageId(parentLi) === "") return null // already root level
    const grandUl = parentLi.parentElement
    const slot = neighbourSlot({
      order: this._orderOf(grandUl), movedIds: [pageId],
      targetId: this._pageId(parentLi), edge: "bottom",
    })
    if (!slot) return null
    return {
      containerId: this._liveContainerId(grandUl),
      beforeId: slot.before_id, afterId: slot.after_id,
      announceKey: "promoted", tokens: {},
    }
  }

  _keyboardDemote(li, pageId) {
    const siblings = this._siblingLis(li.parentElement)
    const prev = siblings[siblings.indexOf(li) - 1]
    if (!prev) return null
    const existing = this._existingChildrenUl(prev)
    const slot = appendSlot({ order: existing ? this._orderOf(existing) : [], movedIds: [pageId] })
    return {
      containerId: this._pageId(prev),
      beforeId: slot.before_id, afterId: slot.after_id,
      announceKey: "demoted", tokens: {},
    }
  }

  _siblingLis(ul) {
    if (!ul) return []
    return Array.from(ul.children).filter((el) => el.tagName === "LI" && el.dataset.workPageId !== "")
  }

  _refocusRow(li) {
    const focusable = li.querySelector("summary, a[href], button, [tabindex]")
    focusable?.focus?.()
  }

  // ── Select (delegated; links navigate instead — the href IS the selection) ─

  _handleClick(event) {
    if (this._gesture) return
    if (event.target?.closest?.("a[href]")) return
    const li = event.target?.closest?.("li[data-work-page-id]")
    const pageId = this._pageId(li)
    if (!li || pageId === "") return
    this._emit(PAGE_SELECT_EVENT, { page_id: pageId })
  }

  // ── Reserved action names (tree_component renders these bindings) ──────────
  // Guarded no-ops: p-dnd's listeners + the ONE monitor above resolve every
  // gesture; these exist so the rendered actions never hit a missing method,
  // and keep the browser's default drag behavior legal. Never publish.

  dragStart() {}

  dragEnd() {}

  dragOver(event) {
    if (event?.cancelable) event.preventDefault()
  }

  dragLeave() {}

  drop(event) {
    if (event?.cancelable) event.preventDefault()
  }
}
